/* @flow */

import axios from 'axios';
import type { $AxiosXHR } from 'axios';
import type { $Application } from 'CoreTypes';
import type { $Configuration } from 'CoreTypes';
import type { $Language } from 'CoreTypes';
import type { $Market } from 'CoreTypes';
import type { $SearchProfile } from 'CoreTypes';
import type { $Translation } from 'CoreTypes';
import type { $Country } from 'CoreTypes';
import type { $LanguageTranslations } from 'CoreTypes/LanguageTranslations';
import ServiceConsumer from './ServiceConsumer';

type $XHRPromise<T> = Promise<$AxiosXHR<T>>;

type $IdObj = {
  id: number
};

export default class CMSConsumer extends ServiceConsumer {

  constructor(configurationServiceEndpoint: string) {
    super(configurationServiceEndpoint);
  }

  getConfiguration = (configId: string, language: string, market: string): $XHRPromise<$Configuration> =>
    axios.get(`${this.endpointUrl}/configuration/externalId/${configId}?market=${market}&language=${language}`);

  getConfigurationById = (id: string): $XHRPromise<$Configuration> =>
    axios.get(`${this.endpointUrl}/configuration/${id}`);

  listConfigurations = (): $XHRPromise<Array<$Configuration>> =>
    axios.get(`${this.endpointUrl}/configuration`);

  createConfiguration = (configuration: Object): $XHRPromise<$IdObj> =>
    axios.post(`${this.endpointUrl}/configuration`, configuration);

  editConfiguration = (configurationId: string, configuration: Object): $XHRPromise<void> =>
    axios.put(`${this.endpointUrl}/configuration/${configurationId}`, configuration);

  deleteConfiguration = (configurationId: string): $XHRPromise<void> =>
    axios.delete(`${this.endpointUrl}/configuration/${configurationId}`);

  createApplication = (application: Object): $XHRPromise<$IdObj> =>
    axios.post(`${this.endpointUrl}/application`, application);

  editApplication = (applicationId: string, application: Object): $XHRPromise<void> =>
    axios.put(`${this.endpointUrl}/application/${applicationId}`, application);

  getApplication = (applicationId: string): $XHRPromise<$Application> =>
    axios.get(`${this.endpointUrl}/application/${applicationId}`);

  listApplications = (): $XHRPromise<Array<$Application>> =>
    axios.get(`${this.endpointUrl}/application`);

  deleteApplication = (applicationId: string): $XHRPromise<void> =>
    axios.delete(`${this.endpointUrl}/application/${applicationId}`);

  createLanguage = (language: Object): $XHRPromise<$IdObj> =>
    axios.post(`${this.endpointUrl}/language`, language);

  editLanguage = (languageId: string, language: Object): $XHRPromise<void> =>
    axios.put(`${this.endpointUrl}/language/${languageId}`, language);

  getLanguage = (languageId: string): $XHRPromise<$Language> =>
    axios.get(`${this.endpointUrl}/language/${languageId}`);

  getLanguageByCode = (languageCode: string): $XHRPromise<$Language> =>
    axios.get(`${this.endpointUrl}/language/code/${languageCode}`);

  listLanguages = (): $XHRPromise<Array<$Language>> =>
    axios.get(`${this.endpointUrl}/language`);

  deleteLanguage = (languageId: string): $XHRPromise<void> =>
    axios.delete(`${this.endpointUrl}/language/${languageId}`);

  listTranslations = (languageId: string): $XHRPromise<Array<$Translation>> =>
    axios.get(`${this.endpointUrl}/language/${languageId}/translations`);

  listTranslationValues = (translationKeys: Array<string>): $XHRPromise<Array<$LanguageTranslations>> =>
    axios.get(`${this.endpointUrl}/language/translations?key=${translationKeys.join(',')}`);

  updateTranslations = (languageId: string, translations: Array<Object>): $XHRPromise<void> =>
    axios.put(`${this.endpointUrl}/language/${languageId}/translations`, translations);

  createMarket = (market: Object): $XHRPromise<$IdObj> =>
    axios.post(`${this.endpointUrl}/market`, market);

  editMarket = (marketId: string, market: Object): $XHRPromise<void> =>
    axios.put(`${this.endpointUrl}/market/${marketId}`, market);

  getMarket = (marketId: string): $XHRPromise<$Market> =>
    axios.get(`${this.endpointUrl}/market/${marketId}`);

  listMarkets = (): $XHRPromise<Array<$Market>> =>
    axios.get(`${this.endpointUrl}/market`);

  deleteMarket = (marketId: string): $XHRPromise<void> =>
    axios.delete(`${this.endpointUrl}/market/${marketId}`);

  listSearchProfiles = (): $XHRPromise<Array<$SearchProfile>> =>
    axios.get(`${this.endpointUrl}/search-profile`);

  createSearchProfile = (searchProfile: Object): $XHRPromise<$IdObj> =>
    axios.post(`${this.endpointUrl}/search-profile`, searchProfile);

  getSearchProfile = (searchProfileId: string): $XHRPromise<$SearchProfile> =>
    axios.get(`${this.endpointUrl}/search-profile/${searchProfileId}`);

  updateSearchProfile = (searchProfileId: string, searchProfile: Object): $XHRPromise<void> =>
    axios.put(`${this.endpointUrl}/search-profile/${searchProfileId}`, searchProfile);

  deleteSearchProfile = (searchProfileId: string): $XHRPromise<void> =>
    axios.delete(`${this.endpointUrl}/search-profile/${searchProfileId}`);

  listCountries = (): $XHRPromise<Array<$Country>> =>
    axios.get(`${this.endpointUrl}/countries`);
}
